import { useEffect, useState } from 'react';
import { useMotionValue } from 'framer-motion';
/**
 *
 * When styling components using the `vh` unit, you get different results on mobile. iOS Safari ignores the UI in the height calculation, but iOS Chrome/Firefox or any other 3th party browser don't. The same is true for Chrome on Android.
 * When the user starts scrolling, the height of the viewport changes for these browsers. This gives a weird results for fullscreen components / sections, because sections above might also change in height, causing the content to be pushed down.
 *
 * You can use this instead of `vh` units to get a more static feel. Be aware, that because it uses javascript and window. It will return a default value on SSR, and on initial render.
 *
 * ```js
 * import { useStaticViewportHeight } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = () => {
 *  const viewportHeight = useStaticViewportHeight(
 *    // Percentage of the static viewport, default is 100% and also uses `${percentage}vh` when no calculation has been made
 *    100,
 *    {
 *      // When to force a height update.
 *      // When going going from 100px => 120px viewport, it will also trigger this hook
 *      percentageChange: 20,
 *
 *      // Return a MotionValue instead of updating the state using useState
 *      // This makes sure that no re-render is triggered
 *      motionValue: false
 *    },
 *  );
 *
 *  return <div style={{ height: viewportHeight }} />;
 * };
 * ```
 *
 * @storybook Hooks/useStaticViewportHeight
 */
const useStaticViewportHeight = (viewportHeight = 100, { percentageChange = 20, motionValue = false } = {}) => {
    const [height, setHeight] = useState(0);
    const motionHeight = useMotionValue(`${viewportHeight}vh`);
    useEffect(() => {
        let previousHeight = 0;
        let previousWidth = 0;
        const updateSize = () => {
            const currentHeight = (window.innerHeight / 100) * viewportHeight;
            const heightDifference = (Math.abs(previousHeight - currentHeight) / previousHeight) * 100;
            const changedToMuch = heightDifference > percentageChange;
            const widthChanged = previousWidth !== window.innerWidth;
            if (previousHeight === 0 || changedToMuch || widthChanged) {
                if (motionValue) {
                    motionHeight.set(`${currentHeight}px`);
                }
                else {
                    setHeight(currentHeight);
                }
                previousHeight = currentHeight;
                previousWidth = window.innerWidth;
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => {
            window.removeEventListener('resize', updateSize);
        };
    }, [motionHeight, motionValue, percentageChange, viewportHeight]);
    if (motionHeight) {
        return motionHeight;
    }
    return height ? `${height}px` : `${viewportHeight}vh`;
};
export default useStaticViewportHeight;
